import * as XLSX from 'xlsx-js-style';
import type { RazmerRow } from '../components/OSRazmerTable';
import { allowedRangeOf } from '../components/OSRazmerTable';
import type { SubprocessOption } from '../select/SubprocessCells';
import type { PhaseTypeOption } from '../select/PhaseTypeSelect';
import { parseDecimalInput, unitPriceExcelFormat } from './decimalInput';

interface ExportArgs {
  rows: RazmerRow[];
  subprocesses: SubprocessOption[];
  phaseTypeOptions: PhaseTypeOption[];
  mainIdent?: string;
  mainName?: string;
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

// 0 is a valid numeric value and must be written as 0 — only a genuinely absent
// value (null/undefined) collapses to a blank cell.
const numOrBlank = (value: number | null | undefined): number | string =>
  value === null || value === undefined ? '' : value;

// Numeric fields on RazmerRow are stored as display-formatted strings (some with
// thousands separators from formatNum in OSRazmerTable) — parse back to a number so
// Excel treats the cell as numeric, then let numOrBlank keep a genuine 0.
const parseNum = (value: string): number | null => parseDecimalInput(value);

// A row carries data if any of its own fields is filled — mirrors hasRowData in
// OSRazmerTable.tsx so trailing empty placeholder rows are dropped from the export.
const rowHasData = (r: RazmerRow): boolean =>
  !!(r.phaseType || r.itemId || r.priority || r.requiredQty || r.allowedWaste || r.allowedYield || r.allowedRangeManual || r.sieve);

export function downloadOSRazmerExcel({
  rows,
  subprocesses,
  phaseTypeOptions,
  mainIdent = '',
  mainName = '',
}: ExportArgs) {
  const subprocessById = new Map(subprocesses.map((sp) => [sp._id, sp]));
  const phaseTypeById = new Map(phaseTypeOptions.map((pt) => [pt._id, pt]));

  const data: Record<string, string | number>[] = rows.filter(rowHasData).map((row) => {
    const subprocess = row.subprocessId ? subprocessById.get(row.subprocessId) : undefined;
    const processDefinition =
      subprocess && typeof subprocess.processDefinition === 'object'
        ? subprocess.processDefinition
        : null;
    const phaseType = row.phaseType ? phaseTypeById.get(row.phaseType) : undefined;

    return {
      'Главна шифра': mainIdent,
      'Главен назив': mainName,
      'Тип на процес': processDefinition?.name || '',
      'Подпроцес': subprocess?.name || '',
      'Тип на фаза': phaseType?.name || '',
      'Приоритет': row.priority || '',
      'Шифра': row.ident || '',
      'Назив': row.name || '',
      'Потребна количина': numOrBlank(parseNum(row.requiredQty)),
      'ЕМ': row.unitCode || '',
      'Кол во KG': numOrBlank(parseNum(row.secondaryQty)),
      'Дозволено отстапување (ЕМ)': allowedRangeOf(row),
      'Дозволен отпад (%)': numOrBlank(parseNum(row.allowedWaste)),
      'Димензија на сито (mm)': numOrBlank(parseNum(row.sieve)),
      'Цена за вреднување': numOrBlank(parseNum(row.valuationPrice)),
      'Последна набавна цена': numOrBlank(parseNum(row.lastPurchasePrice)),
    };
  });

  const headers = [
    'Главна шифра',
    'Главен назив',
    'Тип на процес',
    'Подпроцес',
    'Тип на фаза',
    'Приоритет',
    'Шифра',
    'Назив',
    'Потребна количина',
    'ЕМ',
    'Кол во KG',
    'Дозволено отстапување (ЕМ)',
    'Дозволен отпад (%)',
    'Димензија на сито (mm)',
    'Цена за вреднување',
    'Последна набавна цена',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  // One width per entry in `headers`, in the same order. This array used to hold one entry
  // more than `headers`, which shifted every width by one column from Тип на фаза onward.
  worksheet['!cols'] = [
    { wch: 16 },  // Главна шифра
    { wch: 32 },  // Главен назив
    { wch: 24 },  // Тип на процес
    { wch: 24 },  // Подпроцес
    { wch: 18 },  // Тип на фаза
    { wch: 10 },  // Приоритет
    { wch: 16 },  // Шифра
    { wch: 28 },  // Назив
    { wch: 18 },  // Потребна количина
    { wch: 8 },   // ЕМ
    { wch: 14 },  // Кол во KG
    { wch: 18 },  // Дозволено отстапување (ЕМ)
    { wch: 14 },  // Дозволен отпад (%)
    { wch: 18 },  // Димензија на сито (mm)
    { wch: 16 },  // Цена за вреднување
    { wch: 16 },  // Последна набавна цена
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Q1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Q1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = cellStyle;
        if ([8, 10, 12, 13, 14, 15].includes(c) && typeof worksheet[cellRef].v === 'number') {
          worksheet[cellRef].t = 'n';
          worksheet[cellRef].z = c >= 14 ? unitPriceExcelFormat() : '0.######';
        }
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Размерување');
  XLSX.writeFile(
    workbook,
    `os_razmeruvanje_${safeFilenamePart(mainIdent)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
